<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { refDebounced } from '@vueuse/core';
import {
  UiButton,
  UiChip,
  UiDialog,
  UiIconButton,
  UiInputField,
  UiLinkButton,
  UiProse,
  UiSelect,
  UiTabs,
  UiTabsItem,
  UiText,
  UiTooltip,
} from '@shopexperts/shared';
import type { AdminEmail, AdminEmailStatus } from '@shopexperts/legacy-api';
import {
  useAdminEmailFromProfiles,
  useAdminEmailTagCatalog,
  useAdminEmailsList,
} from '@/app/composables/expert-manager/useAdminEmails';
import EmptyDataPlaceholder from '@/components/common/EmptyDataPlaceholder.vue';
import LoadingCard from '@/components/common/LoadingCard.vue';

const router = useRouter();

const ALL_SENDERS = 'all';

const ALL_STATUSES = 'all';

const searchInput = ref('');
const debouncedSearch = refDebounced(searchInput, 300);
const statusTab = ref(ALL_STATUSES);
const fromFilter = ref(ALL_SENDERS);
const selectedTagIds = ref<string[]>([]);

const { tagOptions, tagNameById } = useAdminEmailTagCatalog();

/** Selected tags render in their own row, so the toolbar keeps a fixed height. */
const selectedTags = computed(() =>
  selectedTagIds.value.map((id) => ({ id, name: tagNameById.value.get(Number(id)) ?? id })),
);

function removeTag(id: string) {
  selectedTagIds.value = selectedTagIds.value.filter((value) => value !== id);
}


const { profiles } = useAdminEmailFromProfiles();
const senderOptions = computed(() => [
  { value: ALL_SENDERS, label: 'All senders' },
  ...profiles.value.map((profile) => ({ value: profile.key, label: `${profile.name} — ${profile.address}` })),
]);

const filters = computed(() => ({
  search: debouncedSearch.value,
  status: statusTab.value === ALL_STATUSES ? '' : statusTab.value,
  from: fromFilter.value === ALL_SENDERS ? '' : fromFilter.value,
  // UiMultiSelectChips speaks strings; the API matches tags by numeric id.
  tagIds: selectedTagIds.value.map(Number).filter((id) => Number.isInteger(id) && id > 0),
}));

const hasFilters = computed(
  () =>
    !!searchInput.value.trim()
    || statusTab.value !== ALL_STATUSES
    || fromFilter.value !== ALL_SENDERS
    || selectedTagIds.value.length > 0,
);

const {
  emails,
  total,
  isLoading,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useAdminEmailsList(filters);

const STATUS_APPEARANCE: Record<AdminEmailStatus, { label: string; bg: string; text: string }> = {
  sent: { label: 'Sent', bg: 'bg-gray-50', text: 'text-gray-500' },
  delivered: { label: 'Delivered', bg: 'bg-sky-50', text: 'text-sky-700' },
  opened: { label: 'Opened', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  bounced: { label: 'Bounced', bg: 'bg-rose-50', text: 'text-rose-600' },
};

function statusAppearance(status: AdminEmailStatus) {
  return STATUS_APPEARANCE[status] ?? STATUS_APPEARANCE.sent;
}

/**
 * The webhook attaches `clicked` and `spam-complaint` itself — they report on
 * the recipient's behaviour, so they read as signals rather than admin labels.
 */
const SYSTEM_TAG_APPEARANCE: Record<string, { icon: string; bg: string; text: string }> = {
  clicked: { icon: 'link-external', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'spam-complaint': { icon: 'close', bg: 'bg-rose-50', text: 'text-rose-600' },
};

function tagAppearance(tag: string) {
  return (
    SYSTEM_TAG_APPEARANCE[tag]
    ?? { icon: undefined, bg: 'bg-indigo-50', text: 'text-indigo-700' }
  );
}

/** A row is one line, so it can only carry a few of the 25 tags an email allows. */
const ROW_TAG_LIMIT = 3;

/**
 * Signals first: a bounce or a complaint is the reason to look at a row at all,
 * so it must never be the tag that gets folded into the `+N`.
 */
function visibleTags(tags: string[]) {
  const signals = tags.filter((tag) => tag in SYSTEM_TAG_APPEARANCE);
  const rest = tags.filter((tag) => !(tag in SYSTEM_TAG_APPEARANCE));
  return [...signals, ...rest].slice(0, ROW_TAG_LIMIT);
}

function hiddenTagCount(tags: string[]) {
  return Math.max(0, tags.length - ROW_TAG_LIMIT);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const selectedEmail = ref<AdminEmail | null>(null);
const detailOpen = ref(false);

function openDetail(email: AdminEmail) {
  selectedEmail.value = email;
  detailOpen.value = true;
}

function goToExpert(email: AdminEmail) {
  if (!email.expert_id) return;
  router.push({ name: 'admin-expert-detail', params: { expertId: email.expert_id } });
}

// Infinite scroll sentinel, same pattern as ExpertManager.
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (el) => {
  if (observer) observer.disconnect();
  if (!el) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: '200px' },
  );
  observer.observe(el);
});
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-secondary p-8 font-light">
    <div class="mb-6 flex flex-col gap-2">
      <div class="flex items-baseline gap-3">
        <h1 class="text-3xl font-normal">Emails</h1>
        <UiText
          v-if="total !== null"
          as="span"
          variant="p-small"
          class="text-se-gray-500"
          :text="`${total} sent`"
        />
      </div>
      <UiText
        as="p"
        variant="p"
        class="max-w-3xl text-se-gray-600"
        text="One-off emails sent from the admin, with delivery, open, and click tracking from Postmark."
      />
    </div>

    <UiTabs v-model="statusTab" :default-value="ALL_STATUSES" class="mb-4">
      <UiTabsItem :value="ALL_STATUSES" label="All" />
      <UiTabsItem
        v-for="(appearance, status) in STATUS_APPEARANCE"
        :key="status"
        :value="status"
        :label="appearance.label"
      />
    </UiTabs>

    <div class="rounded-lg border border-gray-100 bg-white">
      <!-- Toolbar: search and sender live with the list they filter -->
      <div class="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
        <div class="w-72">
          <UiInputField
            v-model="searchInput"
            type="search"
            icon="search"
            placeholder="Search recipient or subject"
          />
        </div>
        <div class="ml-auto flex items-center gap-3">
          <div class="w-52">
            <UiSelect
              v-model="selectedTagIds"
              :options="tagOptions"
              multiple
              placeholder="All tags"
            />
          </div>
          <div class="w-56">
            <UiSelect v-model="fromFilter" :options="senderOptions" placeholder="All senders" />
          </div>
        </div>
      </div>

      <!-- Selected tags get their own row so picking one never reflows the toolbar. -->
      <div
        v-if="selectedTags.length"
        class="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2"
      >
        <UiText as="span" variant="caption" class="uppercase tracking-wide text-gray-500" text="Tags" />
        <span v-for="tag in selectedTags" :key="tag.id" class="inline-flex items-center gap-0.5">
          <UiChip
            :text="tag.name"
            :icon="(tagAppearance(tag.name).icon as any)"
            size="tag"
            rounded="small"
            variant="plain"
            :bg-color-class="tagAppearance(tag.name).bg"
            :text-color-class="tagAppearance(tag.name).text"
          />
          <UiIconButton
            icon="close"
            :aria-label="`Remove tag filter ${tag.name}`"
            class="!p-0.5 [&_svg]:h-3 [&_svg]:w-3"
            @click="removeTag(tag.id)"
          />
        </span>
        <UiText
          v-if="selectedTags.length > 1"
          as="span"
          variant="caption-normal"
          class="text-gray-400"
          text="matching all of these"
        />
        <UiLinkButton
          variant="ghost"
          text-variant="caption-normal"
          hover-decoration="underline"
          label="Clear tags"
          class="ml-auto !py-0 text-gray-500"
          @click="selectedTagIds = []"
        />
      </div>

      <LoadingCard v-if="isLoading" />

      <template v-else-if="emails.length > 0">
        <div class="divide-y divide-gray-100">
          <div
            v-for="email in emails"
            :key="email.id"
            role="button"
            tabindex="0"
            :aria-label="`Open email to ${email.to_email}: ${email.subject}`"
            class="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-se-highlight"
            @click="openDetail(email)"
            @keydown.enter.self.prevent="openDetail(email)"
            @keydown.space.self.prevent="openDetail(email)"
          >
            <!-- Fixed status column: state scans down the left edge like an inbox -->
            <div class="w-24 shrink-0">
              <UiChip
                :text="statusAppearance(email.status).label"
                size="sm"
                variant="plain"
                :bg-color-class="statusAppearance(email.status).bg"
                :text-color-class="statusAppearance(email.status).text"
              />
            </div>

            <div class="flex min-w-0 flex-1 items-baseline gap-2">
              <span class="flex shrink-0 items-center gap-0.5">
                <UiText
                  as="span"
                  variant="p-small"
                  class="font-medium text-gray-900"
                  :text="email.to_email"
                />
                <UiTooltip v-if="email.expert_id" content="Open expert">
                  <UiIconButton
                    icon="link-external"
                    aria-label="Open expert"
                    class="!p-0.5 opacity-0 transition-opacity group-hover:opacity-100 [&_svg]:h-3.5 [&_svg]:w-3.5"
                    @click.stop="goToExpert(email)"
                  />
                </UiTooltip>
              </span>
              <UiText
                as="span"
                variant="caption-normal"
                class="min-w-0 truncate text-gray-500"
                :text="email.subject"
              />
            </div>

            <div class="flex shrink-0 items-center gap-2">
              <UiChip
                v-for="tag in visibleTags(email.tags)"
                :key="tag"
                :text="tag"
                :icon="(tagAppearance(tag).icon as any)"
                size="tag"
                rounded="small"
                variant="plain"
                :bg-color-class="tagAppearance(tag).bg"
                :text-color-class="tagAppearance(tag).text"
              />
              <UiText
                v-if="hiddenTagCount(email.tags)"
                as="span"
                variant="caption-normal"
                class="text-gray-400"
                :text="`+${hiddenTagCount(email.tags)}`"
                :title="email.tags.join(', ')"
              />
              <UiText
                as="span"
                variant="caption-normal"
                class="w-36 text-right text-gray-400"
                :text="formatDateTime(email.created_at)"
              />
            </div>
          </div>
        </div>

        <div ref="sentinel" class="h-1" />

        <div v-if="isFetchingNextPage" class="border-t border-gray-100 py-3 text-center">
          <UiText as="p" variant="p-small" class="text-gray-400">Loading more...</UiText>
        </div>
      </template>

      <div v-else class="px-4 py-10">
        <EmptyDataPlaceholder
          v-if="hasFilters"
          title="No emails match"
          description="Try adjusting your search or filters."
        />
        <EmptyDataPlaceholder
          v-else
          title="No emails sent yet"
          description="Emails sent from the admin (like expert invites) will show up here."
        />
      </div>
    </div>

    <UiDialog v-model="detailOpen" :title="selectedEmail?.subject ?? 'Email'" size="lg">
      <div v-if="selectedEmail" class="flex flex-col gap-5">
        <div class="grid grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <UiText as="p" variant="caption" class="uppercase tracking-wide text-se-gray-500" text="To" />
            <UiText as="p" variant="p-small" class="text-se-dark" :text="selectedEmail.to_email" />
          </div>
          <div>
            <UiText as="p" variant="caption" class="uppercase tracking-wide text-se-gray-500" text="From" />
            <UiText
              as="p"
              variant="p-small"
              class="text-se-dark"
              :text="`${selectedEmail.from_email} (${selectedEmail.from_profile})`"
            />
          </div>
          <div>
            <UiText as="p" variant="caption" class="uppercase tracking-wide text-se-gray-500" text="Reply-To" />
            <UiText as="p" variant="p-small" class="text-se-dark" :text="selectedEmail.reply_to ?? '—'" />
          </div>
          <div>
            <UiText as="p" variant="caption" class="uppercase tracking-wide text-se-gray-500" text="Status" />
            <UiChip
              :text="statusAppearance(selectedEmail.status).label"
              size="sm"
              variant="plain"
              :bg-color-class="statusAppearance(selectedEmail.status).bg"
              :text-color-class="statusAppearance(selectedEmail.status).text"
            />
          </div>
          <div>
            <UiText as="p" variant="caption" class="uppercase tracking-wide text-se-gray-500" text="Sent" />
            <UiText as="p" variant="p-small" class="text-se-dark" :text="formatDateTime(selectedEmail.created_at)" />
          </div>
          <div>
            <UiText as="p" variant="caption" class="uppercase tracking-wide text-se-gray-500" text="Tags" />
            <div v-if="selectedEmail.tags.length" class="mt-0.5 flex flex-wrap gap-1">
              <UiChip
                v-for="tag in selectedEmail.tags"
                :key="tag"
                :text="tag"
                :icon="(tagAppearance(tag).icon as any)"
                size="tag"
                rounded="small"
                variant="plain"
                :bg-color-class="tagAppearance(tag).bg"
                :text-color-class="tagAppearance(tag).text"
              />
            </div>
            <UiText v-else as="p" variant="p-small" class="text-se-dark" text="—" />
          </div>
        </div>

        <div class="rounded-xl border border-se-gray-200 bg-se-gray-50 px-4 py-3">
          <!-- Stored HTML, rendered as the recipient saw it (minus the layout). -->
          <UiProse :html="selectedEmail.body" class="prose-sm max-w-none" />
        </div>
      </div>

      <template #footer>
        <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <UiButton
            v-if="selectedEmail?.expert_id"
            variant="outline"
            label="View expert"
            @click="goToExpert(selectedEmail)"
          />
          <UiButton variant="primary" label="Close" @click="detailOpen = false" />
        </div>
      </template>
    </UiDialog>
  </main>
</template>
